<script lang="ts">
	import type { TrackProperties } from '@openpanel/web';
	import { onMount } from 'svelte';
	import { track } from '$global/utils/track';

	interface Props {
		children?: import('svelte').Snippet;
		storageKey: string;
		maxImpressions: number;
		trackProperties?: TrackProperties;
		threshold?: number;
	}

	let {
		children,
		storageKey,
		maxImpressions,
		trackProperties,
		threshold = 0.8,
	}: Props = $props();

	let trackedImpressionsCount = $state(0);

	let shouldShow = $derived(false);

	let elementRef: HTMLElement | undefined = $state();

	let hasTrackedImpression = $state(false);

	onMount(() => {
		// Some in-app browsers deny storage access and throw a SecurityError:
		// treat the count as 0 so the advert still shows
		try {
			trackedImpressionsCount = Number(sessionStorage.getItem(storageKey)) || 0;
		} catch {
			trackedImpressionsCount = 0;
		}

		shouldShow = trackedImpressionsCount < maxImpressions;
	});

	const handleElementInView = () => {
		if (!hasTrackedImpression && trackedImpressionsCount < maxImpressions) {
			hasTrackedImpression = true;

			track(storageKey, trackProperties);

			try {
				sessionStorage.setItem(storageKey, `${trackedImpressionsCount + 1}`);
			} catch {
				// Storage is blocked: the impression is tracked but not counted
			}
		}
	};

	onMount(() => {
		if (!elementRef) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						handleElementInView();
					}
				});
			},
			{ threshold }
		);

		observer.observe(elementRef);

		// Force an initial visibility check
		setTimeout(() => {
			const rect = elementRef?.getBoundingClientRect();
			if ((rect?.top || 0) < window.innerHeight && (rect?.bottom || 0) > 0) {
				handleElementInView();
			}
		}, 0);

		return () => {
			observer.disconnect();
		};
	});
</script>

<div bind:this={elementRef}>
	{#if shouldShow}
		{@render children?.()}
	{/if}
</div>
